import React from 'react';
import {
  Alert,
  Button,
  Card,
  Collapse,
  Divider,
  Input,
  Space,
  Spin,
  Tag,
  Tooltip,
  Typography
} from 'antd';
import {
  ReloadOutlined,
  SaveOutlined,
  InfoCircleOutlined,
  CopyOutlined
} from '@ant-design/icons';
import { api } from '../api/client';

// Stars — управление чатами звёзд (куда бот шлёт алерты)
// Использует API:
//   GET  /api/stars           -> { ok:true, stars:[{level, chat_id}] }
//   PUT  /api/stars/:level    -> { ok:true }

function normalizeChatId(v) {
  const s = String(v || '').trim();
  if (!s) return '';
  // Разрешаем только цифры и ведущий минус
  if (!/^[-]?[0-9]+$/.test(s)) return s; // вернём как есть — пусть бэк валидирует
  return s;
}

function safeCopy(text) {
  try {
    if (!text) return;
    if (navigator?.clipboard?.writeText) return navigator.clipboard.writeText(String(text));
  } catch (_) {}
}

export default function Stars() {
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [stars, setStars] = React.useState([]);
  const [draft, setDraft] = React.useState({});
  const [savingLevel, setSavingLevel] = React.useState(null);

  const levels = [1, 2, 3, 4, 5, 6];

  const load = React.useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.getStars();
      const arr = Array.isArray(res?.stars) ? res.stars : [];
      setStars(arr);

      const next = {};
      for (const lvl of levels) next[String(lvl)] = '';
      for (const s of arr) {
        if (s && s.level != null) next[String(s.level)] = String(s.chat_id || '');
      }
      setDraft(next);
    } catch (e) {
      setError(e?.message || 'Ошибка загрузки звёзд');
      setStars([]);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    load();
  }, [load]);

  async function save(level) {
    const raw = draft[String(level)];
    const val = normalizeChatId(raw);
    if (!String(val || '').trim()) return;

    setSavingLevel(level);
    setError('');
    try {
      await api.setStarChat(level, val);
      await load();
    } catch (e) {
      setError(e?.message || 'Ошибка сохранения');
    } finally {
      setSavingLevel(null);
    }
  }

  function setValue(level, value) {
    setDraft((d) => ({ ...d, [String(level)]: value }));
  }

  const header = (
    <Space direction="vertical" size={4} style={{ width: '100%' }}>
      <Space align="center" wrap>
        <Typography.Title level={3} style={{ margin: 0 }}>
          ⭐ Чаты звёзд
        </Typography.Title>
        <Tag color="blue">prod-ready</Tag>
      </Space>
      <Typography.Text type="secondary">
        Укажите <b>chat_id</b> групп звёзд — именно сюда бот отправляет SLA-алерты и закрытия.
      </Typography.Text>
      <Typography.Text type="secondary">
        <InfoCircleOutlined /> Подсказка: в чате звезды выполните <code>/ping</code> (или посмотрите лог) — получите
        chat_id.
      </Typography.Text>
    </Space>
  );

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        <Card style={styles.card} bodyStyle={{ padding: 16 }}>
          {header}

          <Divider style={{ margin: '14px 0' }} />

          {error ? (
            <Alert
              type="error"
              showIcon
              message="Ошибка"
              description={error}
              style={{ marginBottom: 12 }}
            />
          ) : null}

          <Spin spinning={loading} tip="Загрузка…">
            <Space direction="vertical" size={10} style={{ width: '100%' }}>
              {levels.map((lvl) => {
                const isSaving = savingLevel === lvl;
                const value = draft[String(lvl)] ?? '';
                const disabled = loading || isSaving;
                const canSave = String(value || '').trim().length > 0 && !disabled;

                const currentFromDb = (() => {
                  const row = (stars || []).find((x) => String(x?.level) === String(lvl));
                  return row?.chat_id ? String(row.chat_id) : '';
                })();

                return (
                  <Card
                    key={lvl}
                    size="small"
                    style={styles.rowCard}
                    bodyStyle={{ padding: 12 }}
                    title={
                      <Space align="center" wrap>
                        <Tag color="gold" style={{ fontWeight: 700 }}>
                          ⭐ {lvl}
                        </Tag>
                        {currentFromDb ? (
                          <Typography.Text type="secondary">
                            Текущее: <code>{currentFromDb}</code>
                          </Typography.Text>
                        ) : (
                          <Typography.Text type="secondary">Не настроено</Typography.Text>
                        )}
                      </Space>
                    }
                    extra={
                      <Space size={8}>
                        <Tooltip title="Скопировать текущее chat_id из БД">
                          <Button
                            size="small"
                            icon={<CopyOutlined />}
                            disabled={!currentFromDb}
                            onClick={() => safeCopy(currentFromDb)}
                          />
                        </Tooltip>
                        <Button
                          type="primary"
                          size="small"
                          icon={<SaveOutlined />}
                          loading={isSaving}
                          disabled={!canSave}
                          onClick={() => save(lvl)}
                        >
                          Сохранить
                        </Button>
                      </Space>
                    }
                  >
                    <Space direction="vertical" size={6} style={{ width: '100%' }}>
                      <Typography.Text type="secondary">
                        Введите chat_id группы звезды (обычно начинается с <code>-100</code>).
                      </Typography.Text>

                      <Input
                        value={value}
                        onChange={(e) => setValue(lvl, e.target.value)}
                        placeholder="например: -1001234567890"
                        allowClear
                        disabled={disabled}
                        size="large"
                      />

                      <Space wrap size={8}>
                        <Button
                          size="small"
                          icon={<CopyOutlined />}
                          disabled={!String(value || '').trim()}
                          onClick={() => safeCopy(String(value || '').trim())}
                        >
                          Скопировать
                        </Button>
                        <Typography.Text type="secondary">
                          Валидация: разрешены только цифры и ведущий минус (остальное проверит бэк).
                        </Typography.Text>
                      </Space>
                    </Space>
                  </Card>
                );
              })}

              <Divider style={{ margin: '6px 0 0 0' }} />

              <Space wrap style={{ width: '100%', justifyContent: 'space-between' }}>
                <Space wrap>
                  <Button icon={<ReloadOutlined />} onClick={load} disabled={loading}>
                    Обновить
                  </Button>
                </Space>
                <Typography.Text type="secondary">
                  Изменения применяются сразу: бот начнёт слать алерты в новые чаты.
                </Typography.Text>
              </Space>

              <Collapse
                size="small"
                items={[
                  {
                    key: 'dbg',
                    label: 'Текущее из БД (debug)',
                    children: (
                      <pre style={styles.pre}>{JSON.stringify(stars, null, 2)}</pre>
                    )
                  }
                ]}
              />
            </Space>
          </Spin>
        </Card>

        <div style={styles.footerHint}>
          <Typography.Text type="secondary">
            Если кнопки «Сохранить» не активны — убедитесь, что ввели <b>chat_id</b> и что вы открыли Mini App под
            админом.
          </Typography.Text>
        </div>
      </div>
    </div>
  );
}

const styles = {
  page: {
    padding: 12
  },
  container: {
    maxWidth: 900,
    margin: '0 auto'
  },
  card: {
    borderRadius: 16
  },
  rowCard: {
    borderRadius: 14
  },
  pre: {
    margin: 0,
    padding: 12,
    borderRadius: 12,
    background: '#0b1220',
    color: '#e5e7eb',
    border: '1px solid rgba(255,255,255,0.08)',
    overflow: 'auto',
    maxHeight: 420
  },
  footerHint: {
    marginTop: 10,
    padding: '0 4px'
  }
};